import { AlertTriangleIcon } from '@/components/ui/icons'

import type { PosFloor, PosTable } from '../types'
import { branchLabel, elapsedLabel, money, needsAttention, tableCardStyle } from './pos-format'

/**
 * The room, as squares.
 *
 * Read from across a counter rather than scanned like a list, which is why a
 * table is a tinted card and not a row with a chip: the question is "which
 * table needs me", and the answer should be visible before anybody reads a
 * word. Grouping comes from the API already ordered — outlet, then zone, then
 * code — so every client draws the same room.
 */
export function FloorGrid({ floors }: { floors: PosFloor[] }) {
  return (
    <div className="flex flex-col gap-8">
      {floors.map((floor) => (
        <section key={floor.branchId} className="flex flex-col gap-4">
          <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-base font-semibold tracking-[-0.01em]">
              {branchLabel(floor.branch)}
            </h2>
            <p className="text-muted text-xs tabular-nums">
              {floor.seatedCount} of {floor.tableCount} seated
            </p>
          </header>

          {floor.zones.map((zone) => (
            <div key={zone.zoneId ?? 'unzoned'} className="flex flex-col gap-2">
              <h3 className="text-muted text-sm font-medium">{zone.name}</h3>

              {/* Fixed-width cards rather than a stretching grid: a table is a
                  physical object of roughly one size, and a room with three of
                  them should not draw them a third of the screen wide. */}
              <div className="flex flex-wrap gap-3">
                {zone.tables.map((table) => (
                  <TableCard key={table.id} table={table} />
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  )
}

function TableCard({ table }: { table: PosTable }) {
  const style = tableCardStyle(table.status)
  const flagged = needsAttention(table.status, table.elapsedSeconds)

  return (
    <article
      className={`relative flex w-36 flex-col gap-1 rounded-xl border p-3 ${style.card} ${
        // A ring rather than a different fill: the state still has to read as
        // itself, with the fact that it has waited too long layered over it.
        flagged ? 'ring-danger/60 ring-2' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="text-lg leading-none font-semibold">{table.code}</span>
        {flagged && (
          <AlertTriangleIcon
            width={14}
            height={14}
            className="text-danger shrink-0"
            aria-label="Waiting longer than it should"
          />
        )}
      </div>

      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${style.text}`}>
        <span className={`size-1.5 rounded-full ${style.dot}`} aria-hidden />
        {style.label}
      </span>

      {table.isSeated ? (
        <>
          <span className="text-muted text-xs tabular-nums">
            {/* Guests over capacity, because a party of 3 at a 4-top and a
                party of 3 at a 2-top are different situations. */}
            {table.guestCount ?? '?'}/{table.seats} guests · {elapsedLabel(table.elapsedSeconds)}
          </span>
          {table.currentOrderTotal !== null && (
            <span className="text-sm font-semibold tabular-nums">
              {money(table.currentOrderTotal)}
            </span>
          )}
        </>
      ) : (
        <span className="text-muted text-xs tabular-nums">
          {table.seats} seats
          {/* A dirty table still counts up — how long it has been waiting to
              be wiped is exactly what a host wants from this card. */}
          {table.elapsedSeconds !== null && ` · ${elapsedLabel(table.elapsedSeconds)}`}
        </span>
      )}
    </article>
  )
}
